import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import PurchaseRequestUpdateModal from '../../components/purchaseRequests/PurchaseRequestUpdateModal';


// import RFQDetailModal from '../../components/rfq/RFQDetailModal';
import { PulseLoader } from "react-spinners";

const PurchaseRequestDetails = () => {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchRequest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/purchase-requests/${id}`);
      setRequest(res.data);
    } catch (err) {
      console.error("Failed to fetch request", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleUpdate = async (updatedData) => {
    try {
      await axios.put(`${config.API_BASE_URL}/purchase-requests/${id}`, updatedData);
      toast.success('Request updated');
      setShowModal(false);
      fetchRequest();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const InfoRow = ({ label, value, fullWidth }) => (
    <div className={fullWidth ? "col-12" : "col-md-6"}>
      <label className="fw-bold fs-9">{label}:</label>
      <p className="form-control-plaintext text-muted">{value || '-'}</p>
    </div>
  );

  const ItemRow = ({ label, value }) => (
    <tr className="d-flex">
      <td className="py-2 lh-sm ps-0 d-flex align-items-center" style={{ flex: '0 0 120px' }}>
        <h5 className="mb-0 me-2 fs-9">{label}</h5>
      </td>
      <td className="py-2 lh-sm fw-bold d-flex align-items-center">:</td>
      <td className="py-2 lh-sm px-3 flex-grow-1 d-flex align-items-center">
        <h5 className="mb-0 fw-normal text-body-secondary fs-9">{value}</h5>
      </td>
    </tr>
  );

  const statusBadgeColor = status => {
    switch (status) {
      case 'requested': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'danger';
      default: return 'secondary';
    }
  };

  const priorityColor = priority => {
    switch (priority) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      default: return 'danger';
    }
  };

  const formatDate = datetime =>
    datetime
      ? new Date(datetime).toLocaleString("en-IN", {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      })
      : '-';

  if (loading) return <Layout><div className="text-center py-5"><PulseLoader color="#014b7cbb" size={9} /></div></Layout>;
  if (!request) return <Layout><p>Request not found.</p></Layout>;

  return (
    <Layout>

      <PurchaseRequestUpdateModal
        show={showModal}
        onHide={() => setShowModal(false)}
        initialData={request}
        onSubmit={handleUpdate}
      />
      <nav className="mb-3" aria-label="breadcrumb">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><Link to="/purchase_requests">Purchase Requests</Link></li>
          <li className="breadcrumb-item active">Request</li>
        </ol>
      </nav>

      <h2 className="mb-4">Purchase Request Details & RFQ</h2>

      <div className="row g-3">
        {/* Left Column - Summary */}
        <div className="col-xl-8">
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light d-flex flex-wrap gap-2 justify-content-between">
              <h5 className="mb-0">
                Request Summary
                <button className="btn btn-link p-0 ms-3" onClick={() => setShowModal(true)}>
                  <i className="fa fa-pen fs-8 text-body-quaternary" />
                </button>
              </h5>
              <span className={`badge badge-phoenix badge-phoenix-${statusBadgeColor(request.status)}`}>
                {request.status}
              </span>
            </div>
            <div className="card-body row g-3">
              <InfoRow label="Requested By" value={request.requested_by?.name} />
              <InfoRow label="Required By Date" value={formatDate(request.required_by_date)} />
              <InfoRow label="Reason" value={request.reason} />
              <InfoRow
                label="Priority"
                value={<span className={`badge text-bg-${priorityColor(request.priority)}`}>{request.priority}</span>}
              />
              <InfoRow label="Notes" value={request.notes} fullWidth />
              <InfoRow label="Created At" value={formatDate(request.created_at)} />
              {request.soId && (
                <InfoRow label="Requested Against" value={request.soId?.orderNumber} />
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Items */}
        <div className="col-xl-4">
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light border-bottom">
              <h5 className="mb-0">Requested Items</h5>
            </div>
            <div className="card-body">
              {request.items?.length > 0 ? (
                <div className="accordion" id="requested-items-accordion">
                  {request.items.map((item, idx) => (
                    <div className="accordion-item border-top" key={item._id}>
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#collapse-${item._id}`}
                          aria-expanded="true"
                          aria-controls={`collapse-${item._id}`}
                        >
                          #{idx + 1} {item.product?.productName || 'Unnamed Product'}
                        </button>
                      </h2>
                      <div
                        id={`collapse-${item._id}`}
                        className="accordion-collapse collapse show"
                        data-bs-parent="#requested-items-accordion"
                      >
                        <div className="accordion-body pt-0">
                          <table className="table table-borderless mb-0">
                            <tbody>
                              <ItemRow label="Quality" value={item.quality} />
                              <ItemRow
                                label="Approx. Quantity"
                                value={`${parseFloat(item.approx_quantity).toFixed(2).replace(/\.0+$/, '')} ${item.quantity_unit}`}
                              />
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No items available.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <PurchaseRequestRFQSection
        requestId={request._id}
      /> */}
    </Layout>
  );
};

export default PurchaseRequestDetails;
